// ═══════════════════════════════════════════════════════════════
// Store Pinia — état d'authentification
// ═══════════════════════════════════════════════════════════════
// Stratégie (conforme Q1 validée) :
//   - accessToken en mémoire JS uniquement (perdu au F5)
//   - refresh_token dans cookie httpOnly géré par le back
//   - bootstrap() au démarrage tente un /refresh : si OK → session
//     restaurée, sinon mode anonyme
//   - logout révoque le refresh côté back + nettoie le state
// ═══════════════════════════════════════════════════════════════

import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { api } from '@/lib/api';
import { setLocale, setCurrency } from '@/lib/i18n';
import { applyBrandAssetsFor } from '@/lib/brand';
import { queryClient } from '@/lib/query-client';

export const useAuthStore = defineStore('auth', () => {
  // ─── State ───────────────────────────────────────────────
  const user = ref(null);
  const tenant = ref(null);
  const accessToken = ref(null);
  const refreshExpiresAt = ref(null);
  // mfaChallenge survit le temps de la 2e étape de login
  const mfaChallenge = ref(null);
  const mfaType = ref(null); // 'totp' | 'email'
  // État du rappel / gate MFA, renvoyé par login/refresh/me (cf. computeMfaState
  // côté back) : { enrolled, policy:'OPTIONAL'|'REQUIRED', required, remind }.
  const mfa = ref(null);
  // bootstrap : true tant que la 1ère tentative de refresh n'est pas terminée
  const bootstrapping = ref(true);
  // Impersonation : session sauvegardée du SUPERADMIN pendant qu'il navigue en tenant
  const impersonationSession = ref(null); // { accessToken, user, tenant }

  // ─── Getters ─────────────────────────────────────────────
  const isAuthenticated = computed(() => !!user.value && !!accessToken.value);
  const needsEmailVerification = computed(
    () => isAuthenticated.value && !user.value?.emailVerifiedAt,
  );
  const role = computed(() => user.value?.role || null);
  const isSuperadmin = computed(() => user.value?.role === 'SUPERADMIN');
  // Mode boutique : user rattaché à un établissement (non-admin) ET revente active.
  // Le PA labo est masqué côté serveur ; le front adapte l'affichage (fiche).
  const isShopMode = computed(
    () => user.value?.isShopScoped === true && tenant.value?.transferResalePricingEnabled === true,
  );
  // User boutique (rôle non privilégié rattaché à ≥1 établissement) — booléen brut
  // renvoyé par le back. Sert à masquer les infos « labo » (ex. coût des recettes),
  // indépendamment de la revente (≠ isShopMode qui exige transferResalePricing).
  const isShopScoped = computed(() => user.value?.isShopScoped === true);
  // Phase R : un RESELLER (hors impersonation) gère son espace revendeur.
  const isReseller = computed(() => user.value?.role === 'RESELLER');
  const isImpersonating = computed(() => !!impersonationSession.value);
  // Gate MFA dur : tenant en politique REQUIRED, user non inscrit. Jamais pour
  // le staff (SUPERADMIN/RESELLER) ni pendant une impersonation (on n'impose pas
  // au staff de configurer le MFA d'un tenant qu'il dépanne).
  const mfaRequired = computed(
    () => !!mfa.value?.required && !isSuperadmin.value && !isReseller.value && !isImpersonating.value,
  );
  // Rappel souple : bandeau dismissable (politique OPTIONAL, snooze expiré).
  const mfaRemind = computed(
    () => !!mfa.value?.remind && !isSuperadmin.value && !isReseller.value && !isImpersonating.value,
  );

  // ─── Internal helpers ────────────────────────────────────
  function applyAuthResponse(data) {
    user.value = data.user;
    tenant.value = data.tenant;
    accessToken.value = data.accessToken;
    refreshExpiresAt.value = data.refreshExpiresAt;
    if (data.mfa !== undefined) mfa.value = data.mfa;
    mfaChallenge.value = null;
    // Appliquer la locale et la devise du tenant
    const locale = data.user?.locale || data.tenant?.defaultLocale;
    if (locale) setLocale(locale);
    if (data.tenant?.currency) setCurrency(data.tenant.currency);
    // Aligne la brand UI (couleurs, logo, favicon) avec celle du tenant connecté.
    if (data.tenant?.brand) applyBrandAssetsFor(data.tenant.brand);
  }

  function clearSession() {
    user.value = null;
    tenant.value = null;
    accessToken.value = null;
    refreshExpiresAt.value = null;
    mfaChallenge.value = null;
    mfa.value = null;
    impersonationSession.value = null;
  }

  // « Plus tard » sur le bandeau de rappel MFA → snooze 7 jours côté back.
  async function snoozeMfaReminder() {
    await api.post('/auth/mfa/snooze');
    if (mfa.value) mfa.value = { ...mfa.value, remind: false };
  }

  // Après enrôlement d'une méthode MFA (TOTP ou email) : lève le rappel/gate
  // sans attendre un refresh complet.
  function markMfaEnrolled() {
    if (mfa.value) mfa.value = { ...mfa.value, enrolled: true, required: false, remind: false };
  }

  // ─── Impersonation ────────────────────────────────────────
  // Sauvegarde la session SUPERADMIN et prend l'identité du tenant admin.
  // Le cookie refresh reste celui du SUPERADMIN — "Quitter" fait un /refresh.
  // sessionStorage persiste l'intention d'impersonation pour qu'un F5 la rejoue
  // automatiquement (cf. bootstrap).
  const IMPERSON_STORAGE_KEY = 'fcos_imperson_tenant_id';
  // Utilisateur impersonné (result.user.id) : persisté pour rejouer EXACTEMENT la
  // même identité au F5 (ex. un MANAGER magasin), pas seulement le 1er admin.
  const IMPERSON_USER_KEY = 'fcos_imperson_user_id';

  function applyImpersonation(result) {
    impersonationSession.value = {
      accessToken: accessToken.value,
      user: user.value,
      tenant: tenant.value,
    };
    user.value = result.user;
    tenant.value = result.tenant;
    accessToken.value = result.accessToken;
    try {
      sessionStorage.setItem(IMPERSON_STORAGE_KEY, result.tenant.id);
      if (result.user?.id) sessionStorage.setItem(IMPERSON_USER_KEY, result.user.id);
    } catch { /* SSR/private mode */ }
    const locale = result.user?.locale || result.tenant?.defaultLocale;
    if (locale) setLocale(locale);
    if (result.tenant?.currency) setCurrency(result.tenant.currency);
    // Bascule l'UI sur la brand du tenant impersonné (Stockzone bleu / FCOS vert)
    applyBrandAssetsFor(result.tenant?.brand || 'fcos');
    // Vide le cache serveur : sans ça, en changeant de tenant impersonné, TanStack
    // Query re-sert les données du tenant précédent (bug du Ctrl+Shift+R). Le token
    // vient d'être remplacé → les refetch repartent sur le nouveau tenant.
    queryClient.clear();
  }

  // Endpoint d'impersonation selon le rôle : un RESELLER passe par son espace
  // (scopé à SES tenants), un SUPERADMIN par le backoffice.
  function impersonatePath(tenantId) {
    return user.value?.role === 'RESELLER'
      ? `/reseller/tenants/${tenantId}/impersonate`
      : `/superadmin/tenants/${tenantId}/impersonate`;
  }

  // Entre en impersonation d'un tenant (SUPERADMIN ou RESELLER).
  // userId optionnel : impersonation d'un utilisateur précis (ex. MANAGER magasin).
  async function enterImpersonation(tenantId, userId = null) {
    const { data } = await api.post(impersonatePath(tenantId), userId ? { userId } : {});
    applyImpersonation(data);
    return data;
  }

  // Restaure directement depuis le snapshot mémorisé à l'entrée en impersonation
  // — PAS de round-trip réseau ici. Un /refresh peut échouer (rotation en cours,
  // latence…) et faisait auparavant tomber tout le monde en clearSession() =
  // déconnexion au lieu d'un retour SUPERADMIN. Si l'access token restauré est
  // périmé, le tout prochain appel API le rafraîchira normalement via l'intercepteur 401.
  async function exitImpersonation() {
    const saved = impersonationSession.value;
    impersonationSession.value = null;
    try {
      sessionStorage.removeItem(IMPERSON_STORAGE_KEY);
      sessionStorage.removeItem(IMPERSON_USER_KEY);
    } catch { /* noop */ }
    if (saved) {
      user.value = saved.user;
      tenant.value = saved.tenant;
      accessToken.value = saved.accessToken;
    } else {
      // Filet de sécurité : ne devrait pas arriver (snapshot toujours posé par
      // applyImpersonation), mais on retente un refresh plutôt que de rester bloqué.
      await bootstrap();
    }
    const locale = user.value?.locale || 'fr';
    setLocale(locale);
    setCurrency('EUR');
    // Retour à la brand initiale (SUPERADMIN n'a pas de tenant → fcos par défaut)
    applyBrandAssetsFor(tenant.value?.brand || 'fcos');
    // Purge le cache du tenant impersonné (mêmes raisons qu'à l'entrée).
    queryClient.clear();
  }

  // ─── Actions auth ────────────────────────────────────────
  // Inscription publique auto-trial (self-service). Le back crée un tenant
  // `trial` et envoie l'email de vérification ; PAS de session : l'accès
  // s'ouvre après clic sur le lien (retour `{ requireEmailVerification, email }`).
  async function signup(payload) {
    const { data } = await api.post('/auth/signup', payload);
    return data;
  }

  // Renvoi du lien de vérification (écran post-inscription / login bloqué).
  async function resendVerification(email) {
    await api.post('/auth/resend-verification', { email });
  }

  async function login(payload) {
    const { data } = await api.post('/auth/login', payload);
    if (data.requireMfa) {
      mfaChallenge.value = data.mfaChallenge;
      mfaType.value = data.mfaType || 'totp';
      return { requireMfa: true, mfaType: data.mfaType || 'totp' };
    }
    applyAuthResponse(data);
    return data;
  }

  async function emailMfaVerify({ code }) {
    if (!mfaChallenge.value) throw new Error('No MFA challenge in progress');
    const { data } = await api.post('/auth/mfa/email/verify', {
      mfaChallenge: mfaChallenge.value,
      code,
    });
    applyAuthResponse(data);
    return data;
  }

  async function emailMfaResend() {
    if (!mfaChallenge.value) throw new Error('No MFA challenge in progress');
    await api.post('/auth/mfa/email/resend', { mfaChallenge: mfaChallenge.value });
  }

  async function mfaVerify({ code }) {
    if (!mfaChallenge.value) throw new Error('No MFA challenge in progress');
    const { data } = await api.post('/auth/mfa/verify', {
      mfaChallenge: mfaChallenge.value,
      code,
    });
    applyAuthResponse(data);
    return data;
  }

  async function mfaRecovery({ recoveryCode }) {
    if (!mfaChallenge.value) throw new Error('No MFA challenge in progress');
    const { data } = await api.post('/auth/mfa/recovery', {
      mfaChallenge: mfaChallenge.value,
      recoveryCode,
    });
    applyAuthResponse(data);
    return data;
  }

  async function oauthGoogle({ idToken }) {
    const { data } = await api.post('/auth/oauth/google', { idToken });
    if (data.requireMfa) {
      mfaChallenge.value = data.mfaChallenge;
      return { requireMfa: true };
    }
    applyAuthResponse(data);
    return data;
  }

  // Déduplication : un SEUL /auth/refresh en vol à la fois, partagé par TOUS les
  // appelants (bootstrap en direct + intercepteur axios). Sans ça, deux refresh
  // parallèles avec le même token (fréquent au F5) provoquent un race de rotation
  // → l'un révoque le token que l'autre rejoue → « reuse detection » → révocation
  // de TOUS les tokens de l'utilisateur (déconnexion intempestive + accumulation).
  let refreshInFlight = null;
  // Rafraîchit l'access token via le cookie refresh — utilisé par api.js + bootstrap
  async function refresh() {
    if (refreshInFlight) return refreshInFlight;
    refreshInFlight = (async () => {
      try {
        const { data } = await api.post('/auth/refresh');
        applyAuthResponse(data);
        return data;
      } catch (e) {
        // Motif de blocage explicite (essai expiré / compte suspendu) : on le
        // mémorise pour que la page de login l'affiche immédiatement après
        // l'éjection, sans attendre une nouvelle tentative de connexion.
        const code = e?.response?.data?.error;
        if (code === 'ERR_TRIAL_EXPIRED' || code === 'ERR_TENANT_SUSPENDED') {
          try { sessionStorage.setItem('fcos_logout_reason', code); } catch { /* private mode */ }
        }
        throw e;
      } finally {
        refreshInFlight = null;
      }
    })();
    return refreshInFlight;
  }

  async function logout() {
    try {
      await api.post('/auth/logout');
    } finally {
      clearSession();
    }
  }

  async function forgotPassword({ email }) {
    await api.post('/auth/forgot-password', { email });
  }

  async function resetPassword({ token, password }) {
    await api.post('/auth/reset-password', { token, password });
  }

  async function verifyEmail({ token }) {
    await api.post('/auth/verify-email', { token });
    if (user.value) user.value.emailVerifiedAt = new Date().toISOString();
  }

  // Au démarrage de l'app : tente un refresh silencieux pour restaurer
  // la session si le cookie httpOnly existe encore. Si un SUPERADMIN était
  // en impersonation avant F5, on rejoue automatiquement l'impersonation.
  async function bootstrap() {
    bootstrapping.value = true;
    try {
      await refresh();
      // Rejouer l'impersonation si persistée en sessionStorage (tenant + user précis)
      let pendingTenantId = null;
      let pendingUserId = null;
      try {
        pendingTenantId = sessionStorage.getItem(IMPERSON_STORAGE_KEY);
        pendingUserId = sessionStorage.getItem(IMPERSON_USER_KEY);
      } catch { /* noop */ }
      // Démarrage à froid via le QR « mode terrain » : l'URL embarque
      // ?t=<tenantId>. Un SUPERADMIN qui ouvre directement cette URL sur un
      // nouvel appareil doit rétablir l'impersonation du tenant ciblé DÈS le
      // bootstrap (avant que la page monte), sinon App.vue le renverrait au
      // backoffice avant même que le guard puisse agir.
      if (!pendingTenantId && user.value?.role === 'SUPERADMIN') {
        try {
          if (/\/inventories\/[^/]+\/mobile\/?$/.test(window.location.pathname || '')) {
            const t = new URLSearchParams(window.location.search).get('t');
            if (t) pendingTenantId = t;
          }
        } catch { /* noop */ }
      }
      if (pendingTenantId && (user.value?.role === 'SUPERADMIN' || user.value?.role === 'RESELLER')) {
        try {
          const { data } = await api.post(impersonatePath(pendingTenantId), pendingUserId ? { userId: pendingUserId } : {});
          applyImpersonation(data);
        } catch {
          // Tenant supprimé / impersonation refusée → on reste SUPERADMIN/RESELLER
          try { sessionStorage.removeItem(IMPERSON_STORAGE_KEY); } catch { /* noop */ }
        }
      }
    } catch {
      clearSession();
      try { sessionStorage.removeItem(IMPERSON_STORAGE_KEY); } catch { /* noop */ }
    } finally {
      bootstrapping.value = false;
    }
  }

  return {
    // state
    user,
    tenant,
    accessToken,
    refreshExpiresAt,
    mfaChallenge,
    mfa,
    bootstrapping,
    // getters
    isAuthenticated,
    needsEmailVerification,
    role,
    isSuperadmin,
    isShopMode,
    isShopScoped,
    isReseller,
    isImpersonating,
    mfaRequired,
    mfaRemind,
    impersonationSession,
    mfaType,
    // actions
    signup,
    login,
    emailMfaVerify,
    emailMfaResend,
    mfaVerify,
    mfaRecovery,
    oauthGoogle,
    refresh,
    logout,
    forgotPassword,
    resetPassword,
    verifyEmail,
    resendVerification,
    bootstrap,
    clearSession,
    snoozeMfaReminder,
    markMfaEnrolled,
    applyImpersonation,
    enterImpersonation,
    exitImpersonation,
  };
});
